import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Server, Database, Bot, Mail, RefreshCw, Loader2, ShieldCheck, ShieldAlert,
} from "lucide-react";
import PageHeader from "@/components/dashboard/PageHeader";
import Button from "@/components/ui/Button";
import { cn } from "@/lib/cn";
import { useApiData } from "@/hooks/useApiData";
import { getHealth } from "@/api/health";
import { getAiConfig } from "@/api/admin";
import LoadError from "@/components/dashboard/LoadError";

// "13:05:22" in the viewer's own timezone, built by hand rather than through
// toLocaleTimeString() for the reason lib/dates.js gives: a locale decides
// separators and padding, and this output must not vary by machine. Seconds are
// included because two refreshes a few seconds apart have to look different —
// minute resolution would make a working refresh look like a dead one again.
const pad = (n) => String(n).padStart(2, "0");
const clockTime = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;

export default function System() {
  const { data, loading, demo, error, reload } = useApiData(getHealth, null);
  // The AI provider isn't part of /health, so this card used to just mirror
  // apiUp and claim "Operational" with no AI configured at all. Read the real
  // provider config instead.
  const { data: aiCfg, error: aiError, reload: reloadAi } = useApiData(getAiConfig, null);
  const [refreshing, setRefreshing] = useState(false);
  // When the figures on screen were last read from the server. Health rarely
  // changes, so without this a successful refresh is indistinguishable from a
  // dead button — which is exactly how it was reported.
  const [checkedAt, setCheckedAt] = useState(null);

  // Refresh re-pulls EVERY source this page shows, and waits for them.
  //
  // It used to fire the reloads and return immediately, so nothing knew a
  // refresh was in progress: the button never disabled, the icon never moved,
  // and a second click started an overlapping set of requests whose responses
  // could land out of order and redisplay the older one. Awaiting them is what
  // makes the in-flight state — and the guard against duplicate clicks — honest.
  // useApiData.load resolves for both success and failure, so neither branch can
  // leave the button stuck spinning.
  const refreshAll = useCallback(async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      await Promise.all([reload(), reloadAi()]);
    } finally {
      setRefreshing(false);
    }
  }, [refreshing, reload, reloadAi]);

  // Stamped when health data actually arrives — mount and every refresh — so it
  // reports when the DISPLAYED values were read, not when a button was pressed.
  useEffect(() => {
    if (data) setCheckedAt(new Date());
  }, [data]);

  const apiUp = !demo && !!data && data.status === "ok";
  const dbUp = !!data && data.database === "up";
  const smtpUp = !!data && data.email === "up";
  // Field names per GET /admin/ai-config: `key_set` is Anthropic's.
  const aiReady = !!aiCfg && !!(aiCfg.key_set || aiCfg.openai_key_set || aiCfg.ollama_key_set);
  const allOk = apiUp && dbUp;

  const cards = [
    { icon: Server, label: "API server", value: apiUp ? "Operational" : "Down", ok: apiUp },
    { icon: Database, label: "Database", value: data ? (dbUp ? "Operational" : "Down") : "Checking…", ok: dbUp },
    // A failed /admin/ai-config read is neither "configured" nor "still loading":
    // without its own state the card sat on "Checking…" behind a red dot forever.
    { icon: Bot, label: "AI service", value: aiError ? "Unknown" : aiCfg ? (aiReady ? "Configured" : "No provider key") : "Checking…", ok: aiReady, warn: !!aiError || (!!aiCfg && !aiReady) },
    { icon: Mail, label: "SMTP", value: data ? (smtpUp ? "Configured" : "Not configured") : "Checking…", ok: smtpUp, warn: !!data && !smtpUp },
  ];

  return (
    <>
      <PageHeader
        title="System Health"
        subtitle="Backend service status."
        actions={(
          <Button variant="outline" size="sm" onClick={refreshAll} disabled={refreshing}>
            <RefreshCw size={16} className={cn(refreshing && "animate-spin")} />
            {refreshing ? "Refreshing…" : "Refresh"}
          </Button>
        )}
      />

      <LoadError message={error} onRetry={reload} />

      {/* Only the FIRST load gets the full-page spinner — Refresh used to blank
          the whole page and rebuild it on every click. */}
      {loading && !data ? (
        <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 size={26} className="animate-spin" /></div>
      ) : error ? null : (
        <>
          {/* overall status hero */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className={cn(
              "card mb-5 flex flex-col gap-4 overflow-hidden p-5 sm:flex-row sm:items-center sm:justify-between",
              allOk ? "border-emerald-200/70 dark:border-emerald-500/20" : "border-amber-200/70 dark:border-amber-500/20"
            )}
          >
            <div className="flex items-center gap-4">
              <span className={cn("grid h-14 w-14 shrink-0 place-items-center rounded-2xl", allOk ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-300" : "bg-amber-500/10 text-amber-600 dark:text-amber-300")}>
                {allOk ? <ShieldCheck size={26} /> : <ShieldAlert size={26} />}
              </span>
              <div>
                <p className="font-display text-xl font-bold text-slate-900 dark:text-white">
                  {allOk ? "All systems operational" : "Service disruption"}
                </p>
                <p className="mt-0.5 flex items-center gap-2 text-sm text-slate-400">
                  <span className={cn("inline-block h-2 w-2 rounded-full", allOk ? "bg-emerald-500" : "bg-amber-500", "animate-pulse")} />
                  {apiUp ? "Live · " : "Offline · "}
                  source <code className="rounded bg-slate-100 px-1 py-0.5 dark:bg-white/10">GET /health</code> · v{data?.version || "—"}
                  {checkedAt && ` · checked ${clockTime(checkedAt)}`}
                </p>
              </div>
            </div>
            {(demo || !apiUp) && (
              <span className="inline-flex items-center gap-1.5 rounded-xl border border-rose-200 bg-rose-50/70 px-3 py-2 text-xs font-semibold text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-200">
                <Server size={14} /> Start the backend on :8000
              </span>
            )}
          </motion.div>

          {/* service cards */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {cards.map((c, i) => (
              <motion.div key={c.label} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} whileHover={{ y: -3 }} className="card p-5">
                <div className="flex items-center justify-between">
                  <span className="grid h-9 w-9 place-items-center rounded-xl bg-slate-100 text-slate-500 dark:bg-white/10 dark:text-slate-300"><c.icon size={18} /></span>
                  <span className={cn("h-2.5 w-2.5 rounded-full", c.warn ? "bg-amber-400" : c.ok ? "bg-emerald-500" : "bg-rose-500", (c.ok || c.warn) && "animate-pulse")} />
                </div>
                <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-slate-400">{c.label}</p>
                <p className={cn("mt-0.5 font-semibold", c.warn ? "text-amber-500 dark:text-amber-400" : c.ok ? "text-emerald-500 dark:text-emerald-400" : "text-rose-500 dark:text-rose-400")}>{c.value}</p>
              </motion.div>
            ))}
          </div>
        </>
      )}
    </>
  );
}
